import { WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { Model } from 'src/app/interfaces/api/model';
import { Track } from 'src/app/interfaces/api/track';
import { ModelService } from 'src/app/services/api/models/model.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { UnitShape } from '../shapes/unit';
import { TrackService } from './../../../services/api/tracks/track.service';

const Autodesk = (window as any).Autodesk;

interface PlayerData {
    startDate: Date;
    endDate: Date;
}

export class UnitDockingPanel extends Autodesk.Viewing.UI.DockingPanel {
    // Table properties
    unitTableId: string = "1234";

    // Core dependencies
    private viewer: any;
    private player: PlayerData | null = null;
    private playerData: BehaviorSubject<PlayerData>;

    // Signal-driven
    private unitSignal?: WritableSignal<UnitShape[]>;

    // DOM elements
    private unitTable!: HTMLTableElement;
    private unitTableBody!: HTMLTableSectionElement;
    private loadAllTrace!: HTMLDivElement;
    private iconSize!: HTMLDivElement;

    // Services
    private unitService: UnitService;
    private trackService: TrackService;
    private translate: TranslateService;
    private modelService: ModelService;

    // UI components
    private toolsPanel: { setVisible: (visible: boolean) => void } | null = null;

    // Callback functions
    private updateFromTrack: (track: Track) => void;
    private onScaleDividerChange: (value: number) => void = () => {};
    private scaleDivider: number = 25;

    // Model and geometry properties
    private model: Model;
    private fromObjectPanel: { setVisible: (visible: boolean) => void } | null = null;
    private accuracy: number = 1;

    get units(): UnitShape[] {
        return this.unitSignal ? this.unitSignal() : [];
    }

    constructor(
        viewer: any,
        container: HTMLElement,
        id: string,
        title: string,
        options: any
    ) {
        super(container, id, title, options);

        this.viewer = viewer;
        this.unitService = options.unitService;
        this.modelService = options.modelService;
        this.unitService = options.unitService;
        this['trackerService'] = options.trackerService;
        this.trackService = options.trackService;
        this.translate = options.translate;
        this.playerData = options.playerData;
        this.player = options.player;
        this.updateFromTrack = options.updateFromTrack;
        this.scaleDivider = options.unitScaleMultiplier ?? 1;
        if (options.onScaleDividerChange) this.onScaleDividerChange = options.onScaleDividerChange;

        this.edit = this.edit.bind(this);

        this.model = options.model;
        this.unitSignal = options.unitSignal;

        if (!this.unitSignal && options.units) {
            console.warn('⚠️ UnitDockingPanel: No unitSignal provided, using fallback units array');
        }

        this['projectXY'] = options.projectXY;
        this['projectLatLonXY'] = options.projectLatLonXY;
        this['updateUnits'] = options.updateUnits;
        this['offset'] = options.offset;

        (this['container'] as HTMLElement).style.top = "10px";
        (this['container'] as HTMLElement).style.left = "10px";
        (this['container'] as HTMLElement).style.width = "540px";
        (this['container'] as HTMLElement).style.height = "443px";
        (this['container'] as HTMLElement).style.resize = "auto";

        this.unitTableId = 'units-table';

        const div = document.createElement('div');
        div.classList.add('docking-panel-container-solid-color-a', 'docking-panel-scroll');
        div.style.height = "calc(100% - 60px)";
        div.style.overflowX = "hidden";

        this.loadAndRenderHtml(div).then(() => {
            (this['container'] as HTMLElement).appendChild(div);

            this.unitTable = div.querySelector('#unit-table') as HTMLTableElement;
            this.unitTableBody = div.querySelector('#unit-table-body') as HTMLTableSectionElement;

            this.iconSize = div.querySelector('#icon-size') as HTMLDivElement;
            this.loadAllTrace = div.querySelector('#load-all-trace') as HTMLDivElement;

            this.init();
        });
    }

    public init(reload: boolean = false): void {
        if (this.fromObjectPanel) {
            this.fromObjectPanel.setVisible(false);
        }
        if (this.toolsPanel) {
            this.toolsPanel.setVisible(false);
        }

        this.unitTableBody.innerHTML = "";

        const currentUnits = this.units;
        currentUnits.forEach((u: UnitShape) => this.add(u));

        const selectAllUnits = document.getElementById("select-all-unit");
        if (selectAllUnits) {
            selectAllUnits.removeEventListener('click', this.selectAllUnits);
            selectAllUnits.addEventListener('click', this.selectAllUnits);
        }

        const selectAllColliders = document.getElementById("select-all-collider");
        if (selectAllColliders) {
            selectAllColliders.removeEventListener('click', this.selectAllColliders);
            selectAllColliders.addEventListener('click', this.selectAllColliders);
        }

        const selectAllTraces = document.getElementById("select-all-trace");
        if (selectAllTraces) {
            selectAllTraces.removeEventListener('click', this.selectAllTraces);
            selectAllTraces.addEventListener('click', this.selectAllTraces);
        }

        const iconScale = document.getElementById("icon-scale");
        if (iconScale) {
            iconScale.removeEventListener('change', (event: Event) => {
                const target = event.target as HTMLInputElement;
                this.onIconScaleChange(parseFloat(target.value));
            });
            iconScale.addEventListener('change', (event: Event) => {
                const target = event.target as HTMLInputElement;
                this.onIconScaleChange(parseFloat(target.value));
            });
        }

        const getTraces = document.getElementById("load-trace-btn");
        if (getTraces) {
            getTraces.removeEventListener('click', () => this.getTraces());
            getTraces.addEventListener('click', () => this.getTraces());
        }

        const accuracySelect = document.getElementById("accuracy");
        if (accuracySelect) {
            accuracySelect.removeEventListener('change', (event: Event) => {
                const target = event.target as HTMLSelectElement;
                this.accuracy = parseInt(target.value, 10);
            });
            accuracySelect.addEventListener('change', (event: Event) => {
                const target = event.target as HTMLSelectElement;
                this.accuracy = parseInt(target.value, 10);
            });
        }

        const editCells = document.getElementsByClassName('edit') as HTMLCollectionOf<HTMLElement>;
        for (let i = 0; i < editCells.length; i++) {
            const cell = editCells[i];

            if (cell) {
                cell.addEventListener('change', (event: Event) => {
                    this.edit(event);
                });
            }
        }
    }

    private onIconScaleChange(value: number): void {
        this.scaleDivider = value;
        this.onScaleDividerChange(value);
    }

    private getTraces(): void {
        let trackers: string[] = [];
        let alertDisplayed = false;
        let n = trackers.length;

        const currentUnits = this.units;

        currentUnits.forEach((u: UnitShape) => {
            if (u.trackers && u.trackers.length && u.showTrace) {
                trackers = trackers.concat(u.trackers.map((t: { id: string }) => t.id));
            }
        });

        this.player = this.playerData.getValue();

        if (!this.player) return;

        trackers.forEach((tracker: string) => {
            this.trackService.getAllByTimePeriod({
                tracker,
                startDate: this.player!.startDate.toISOString(),
                endDate: this.player!.endDate.toISOString(),
                accuracy: this.accuracy
            }).subscribe({
                next: (results: Track[]) => {
                    n--;

                    if (!alertDisplayed && results.length >= 10000) {
                        alert();
                        alertDisplayed = true;
                    } else if (!alertDisplayed && n === 0) {
                        // Handle completion
                    }

                    results.sort((a: Track, b: Track) =>
                        moment(a.createdDate).diff(moment(b.createdDate))
                    );

                    results.forEach((r: Track) => {
                        this.updateFromTrack(r);
                    });
                }, error: (err: any) => {
                    console.error(err);
                    n--;
                }
            });
        });
    }

    private selectAllUnits = (event: Event): void => {
        const checked = (event.target as HTMLInputElement).checked;
        const unitCheckboxes = document.getElementsByClassName('unit-checkbox') as HTMLCollectionOf<HTMLInputElement>;

        for (let i = 0; i < unitCheckboxes.length; i++) {
            const cell = unitCheckboxes[i];
            if (!cell) continue;

            if (cell.checked !== checked) {
                cell.checked = checked;
                this.edit({ target: cell } as any);
            }
        }
    }

    private selectAllColliders = (event: Event): void => {
        const checked = (event.target as HTMLInputElement).checked;
        const colliderCheckboxes = document.getElementsByClassName('collider-checkbox') as HTMLCollectionOf<HTMLInputElement>;

        for (let i = 0; i < colliderCheckboxes.length; i++) {
            const cell = colliderCheckboxes[i];
            if (!cell) continue;

            if (cell.checked !== checked) {
                cell.checked = checked;
                this.edit({ target: cell } as any);
            }
        }
    }

    private selectAllTraces = (event: Event): void => {
        const checked = (event.target as HTMLInputElement).checked;
        const traceCheckboxes = document.getElementsByClassName('trace-checkbox') as HTMLCollectionOf<HTMLInputElement>;

        for (let i = 0; i < traceCheckboxes.length; i++) {
            const cell = traceCheckboxes[i];
            if (!cell) continue;

            if (cell.checked !== checked) {
                cell.checked = checked;
                this.edit({ target: cell } as any);
            }
        }
    }

    private add(unit: UnitShape): void {
        const showChecked = unit.show ? 'checked' : '';
        const colliderChecked = unit.showCollider ? 'checked' : '';
        const traceChecked = unit.showTrace ? 'checked' : '';

        const row = document.createElement('tr');
        row.classList.add('adsk-searchbox');
        row.id = `row-${unit.id}`;

        row.innerHTML = `
                <td class="unit-show">
                    <input type="checkbox" class="edit form-control input-sm unit-checkbox" tracker="${unit.id}" ${showChecked}>
                </td>
                <td class="unit-name">
                    ${unit.name}
                </td>
                <td class="unit-showCollider">
                    <input type="checkbox" class="edit form-control input-sm collider-checkbox" tracker="${unit.id}" ${colliderChecked}>
                </td>
                <td class="unit-traceColor">
                    <input type="hidden" class="edit" value="${unit.color}">
                    <div class="color-swatches">
                        <span class="color-swatch ${unit.color === 'danger'  ? 'active' : ''}" data-color="danger"  style="background:#dd4b39" title="Red"></span>
                        <span class="color-swatch ${unit.color === 'warning' ? 'active' : ''}" data-color="warning" style="background:#FF6600" title="Orange"></span>
                        <span class="color-swatch ${unit.color === 'primary' ? 'active' : ''}" data-color="primary" style="background:#3c8dbc" title="Blue"></span>
                        <span class="color-swatch ${unit.color === 'success' ? 'active' : ''}" data-color="success" style="background:#00a65a" title="Green"></span>
                        <span class="color-swatch ${unit.color === 'lemon'   ? 'active' : ''}" data-color="lemon"   style="background:#f3e612" title="Yellow"></span>
                        <span class="color-swatch ${unit.color === 'teal'    ? 'active' : ''}" data-color="teal"    style="background:#39CCCC" title="Teal"></span>
                        <span class="color-swatch ${unit.color === 'fuchsia' ? 'active' : ''}" data-color="fuchsia" style="background:#F012BE" title="Fuchsia"></span>
                        <span class="color-swatch ${unit.color === 'info'    ? 'active' : ''}" data-color="info"    style="background:#666666" title="Grey"></span>
                        <span class="color-swatch ${unit.color === 'dark'    ? 'active' : ''}" data-color="dark"    style="background:#010101" title="Black"></span>
                        <span class="color-swatch ${unit.color === 'muted'   ? 'active' : ''}" data-color="muted"   style="background:#808081" title="Muted"></span>
                    </div>
                </td>
                <td class="unit-showTrace">
                    <input type="checkbox" class="edit form-control input-sm trace-checkbox" tracker="${unit.id}" ${traceChecked}>
                </td>`;

        this.unitTableBody.appendChild(row);

        row.querySelectorAll('.color-swatch').forEach(swatch => {
            swatch.addEventListener('click', () => {
                const color = (swatch as HTMLElement).getAttribute('data-color')!;
                const hiddenInput = row.querySelector('.unit-traceColor .edit') as HTMLInputElement;
                if (hiddenInput) {
                    hiddenInput.value = color;
                    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
                }
                row.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('active'));
                swatch.classList.add('active');
            });
        });
    }

    private edit(e: any): void {
        const input = e.target as HTMLInputElement | HTMLSelectElement;
        const value = (input as HTMLInputElement).value;
        const checked = (input as HTMLInputElement).checked;

        const id = (input.parentNode?.parentNode as HTMLElement)?.getAttribute('id')?.split("-")[1];
        if (!id) return;

        const attrName = (input.parentNode as HTMLElement)?.getAttribute("class")?.split("-")[1];
        if (!attrName) return;

        const currentUnits = this.units;

        const unit = currentUnits.find((u: UnitShape) => u.id === id);
        if (!unit) return;

        if (unit.show === undefined) {
            unit.show = unit.visible || true;
        }
        if (unit.geometry === undefined && unit.collider?.geometry) {
            unit.geometry = unit.collider.geometry;
        }

        switch (attrName) {
            case 'show':
                unit.show = checked;
                unit.visible = checked;

                if (this.viewer && this.viewer.impl) {

                    if (checked) {
                        if (unit.mesh) {
                            unit.mesh.visible = true;
                            this.viewer.impl.addOverlay(unit.color, unit.mesh);
                        }
                    } else {
                        if (unit.mesh) {
                            this.viewer.impl.removeOverlay(unit.color, unit.mesh);
                            unit.mesh.visible = false;
                        }
                    }

                    this.viewer.impl.invalidate(true, true, true);
                    this.viewer.impl.sceneUpdated(true);
                }
                break;

            case 'showCollider':
                unit.showCollider = checked;
                if (unit.collider) {
                    unit.collider.visible = checked;
                }

                if (unit.colliders && this.viewer && this.viewer.impl) {
                    unit.colliders.forEach((c: any) => {
                        if (c.mesh) {
                            c.mesh.visible = checked;

                            if (checked) {
                                this.viewer.impl.addOverlay('collider', c.mesh);
                            } else {
                                this.viewer.impl.removeOverlay('collider', c.mesh);
                            }
                        }
                    });

                    this.viewer.impl.invalidate(true, true, true);
                    this.viewer.impl.sceneUpdated(true);
                }
                break;

            case 'traceColor':
                const oldColor = unit.color;
                unit.traceColor = value;
                unit.color = value;

                if (this.viewer && this.viewer.impl && unit.mesh) {
                    this.viewer.impl.removeOverlay(oldColor, unit.mesh);

                    if (unit.show) {
                        this.viewer.impl.addOverlay(value, unit.mesh);
                    }

                    this.viewer.impl.invalidate(true, true, true);
                    this.viewer.impl.sceneUpdated(true);
                }
                break;

            case 'showTrace':
                if (!checked && unit.tracePath) {
                    while (unit.tracePath.length) {
                        const line_mesh = unit.tracePath.pop();
                        if (this.viewer && this.viewer.impl) {
                            this.viewer.impl.removeOverlay('trace-' + unit.traceColor, line_mesh);
                        }
                    }
                }

                unit.trace = checked;
                unit.showTrace = checked;

                if (this.viewer && this.viewer.impl) {
                    this.viewer.impl.invalidate(true, true, true);
                    this.viewer.impl.sceneUpdated(true);
                }
                break;

            default:
                break;
        }

        this.updateUnit(unit.id);
    }

    private updateUnit(id: string): void {
        const currentUnits = this.units;
        let unit = currentUnits.find((u: UnitShape) => u.id === id);

        if (unit) {
            const unitData = {
                id: unit.id,
                name: unit.name,
                show: unit.show,
                visible: unit.visible,
                showCollider: unit.showCollider,
                showTrace: unit.showTrace,
                trace: unit.trace,
                color: unit.color,
            };

            this.unitService.updateUnit(unitData).subscribe({
                next: (res: any) => {
                    console.log('✅ Unit properties updated successfully');
                },
                error: (err: any) => {
                    console.error('❌ Error updating unit:', err);
                }
            });
        }
    }

    async loadAndRenderHtml(div: HTMLDivElement) {
        const template = await this.loadHtmlTemplate('viewer/units/unit-extension/unit-extension.html');
        const variables = {
            viewer_add_unit: this.translate.instant('viewer.add_unit'),
            datatable_name: this.translate.instant('datatable.name'),
            viewer_icon_scale: this.translate.instant('viewer.icon_scale'),
            icon_scale: this.scaleDivider,
            viewer_trace_accuracy: this.translate.instant('viewer.trace_accuracy'),
            viewer_load_trace: this.translate.instant('viewer.load_trace'),
            viewer_show: this.translate.instant('datatable.visible'),
            datatable_collider: this.translate.instant('datatable.collider'),
            datatable_trace: this.translate.instant('datatable.trace'),
        };

        div.innerHTML = this.renderTemplate(template, variables);
    }

    async loadHtmlTemplate(path: string): Promise<string> {
        const response = await fetch(path);
        return await response.text();
    }

    renderTemplate(template: string, variables: Record<string, string | number>) {
        return template.replace(/{{(.*?)}}/g, (_, key) => String(variables[key.trim()] ?? ''));
    }
}